import { useMemo } from 'react';
import { QuickFillHooks } from '@frontend/api-quick-fill';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useSchedulingLocationInfo } from '@frontend/schedule';
import { useScopedAppFlagStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';

export const useScheduleNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const { getCFLocationMap } = useSchedulingLocationInfo();
  const { hasQuickFillFeature } = QuickFillHooks.useQuickFillFeatureDetails();
  const { hasAiScheduleAccess } = useAiScheduleAccess();
  const { isFeatureHiddenInAllLocation } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const isEligibilityListViewFFEnabled = getFeatureFlagValue('insurance-eligibility-list-view');
  const onlineSchedulingCFLocationMap = getCFLocationMap('onlinescheduling');
  const isOnlineSchedulingEnabledForAnyLocation = Object.values(onlineSchedulingCFLocationMap).some((value) => !!value);
  const hasAccessToEligibilityList = isEligibilityListViewFFEnabled && !isFeatureHiddenInAllLocation;

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          label: t('Calendar'),
          relativePath: '/calendar',
          trackingId: 'nav-schedule-calendar',
          description: t(
            'Visual scheduling interface for appointments, out of office events, and other time-based events. Offers day view only by provider.'
          ),
        },
        {
          label: t('Availability Finder'),
          relativePath: '/availability',
          trackingId: 'nav-schedule-availability',
          show: hasAiScheduleAccess,
          description: t('Preview the schedule for a selected service.'),
        },
        {
          label: t('Eligibility List'),
          relativePath: '/eligibility-list',
          trackingId: 'nav-schedule-eligibility-list',
          show: hasAccessToEligibilityList,
          description: t('View a list of upcoming appointments and their insurance eligibility data.'),
        },
        {
          label: t('Request History'),
          relativePath: '/request-history',
          show: isOnlineSchedulingEnabledForAnyLocation,
          trackingId: 'nav-schedule-request-history',
          description: t(
            'Displays historical appointment requests and their statuses. Useful for follow-up and tracking patient inquiries.'
          ),
        },
        {
          show: hasQuickFillFeature,
          label: t('Waitlist History'),
          relativePath: '/quickfill-history',
          trackingId: 'nav-schedule-quickfill-history',
          description: t(
            'Records the activity and notifications related to filling canceled appointment slots with new patients quickly.'
          ),
        },
        {
          show: isDebugModeOn,
          label: t('Debug'),
          relativePath: '/debug',
          trackingId: 'nav-schedule-debug',
          description: t(
            'Access debugging tools and options for the scheduling module. Intended for developers and advanced users.'
          ),
        },
      ]),
    [
      t,
      isOnlineSchedulingEnabledForAnyLocation,
      hasQuickFillFeature,
      isDebugModeOn,
      hasAiScheduleAccess,
      hasAccessToEligibilityList,
    ]
  );

  return useMemo(
    () =>
      createNavItem('schedule', {
        iconName: 'calendar',
        label: t('Schedule'),
        root: '/schedule',
        subNavItems,
        settings: { path: '/schedule', label: '' },
      }),
    [t, subNavItems]
  );
};
